import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faEye,
  faStar,
  faForwardStep,
  faClipboardCheck,
  faNoteSticky,
} from '@fortawesome/free-solid-svg-icons';
import './Submitclasseslegacy.css'; // LEGACY: old global classes other old pages may use
import './SubmitClasses.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* Same "system" teacher id as before for skipped classes */
const SKIP_TEACHER_ID = '26c1564d-f731-40d7-ab0a-47eaad4ebec8';

const EMPTY_FORM = {
  IdModule: '',
  IdClass: '',
  SubmitClass: 0,
  Rating: '',
  Details: '',
  approved: 0,
  IdClassStatus: 0,
  fullpay: 0,
  SessionRate: '',
  SessionFees: '',
  TeacherDuration: 0,
  StudentDuration: 0,
  AdminEvaluation: '',
};
const EMPTY_CREATE = { IdModule: '', IdClasses: [] }; // skip several projects of a module at once

const PAY = [
  [0, 'Full pay', 'is-good'],
  [1, 'Half pay', 'is-warn'],
  [2, 'No pay', 'is-bad'],
];
const payOf = (v) => PAY.find(([n]) => n === Number(v)) || [null, 'Not set', 'is-info'];

/* Same date conversion as before (date-fns, browser time) */
const parse = (s) => {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};
const fmtDateTime = (s) => {
  const d = parse(s);
  return d ? format(d, 'MMM dd, yyyy hh:mm a') : s || '—';
};

/* seconds -> MM:SS (same as before) */
const formatDurationDisplay = (seconds) => {
  if (!seconds || seconds === 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/* MM:SS -> seconds (same as before) */
const parseDurationToSeconds = (durationStr) => {
  if (!durationStr || durationStr === '00:00') return 0;
  const parts = durationStr.split(':');
  if (parts.length === 2) {
    const mins = parseInt(parts[0], 10) || 0;
    const secs = parseInt(parts[1], 10) || 0;
    return mins * 60 + secs;
  }
  return parseInt(durationStr, 10) || 0;
};
const DURATION_RE = /^\d{0,3}:?\d{0,2}$/;

/* Class status: 0 = Completed, 1 = Not completed. Empty counts as 0, same as the edit form
   always did, so the details view and the edit form now agree. */
const classStatus = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));
const classStatusLabel = (v) => {
  const n = classStatus(v);
  if (n === 0) return 'Completed';
  if (n === 1) return 'Not completed';
  return `Status ${v}`;
};

const errText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/* Small pieces */
const PayPill = ({ value }) => {
  const [, label, tone] = payOf(value);
  return <span className={`rmasb-pill ${tone}`}>{label}</span>;
};

const Stars = ({ value }) => {
  const n = Number(value) || 0;
  return (
    <span className="rmasb-stars" aria-label={`${n} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <FontAwesomeIcon key={i} icon={faStar} className={i <= n ? 'is-on' : ''} />
      ))}
    </span>
  );
};

const Switch = ({ on, onChange, label, tone }) => (
  <label className={`rmasb-switch ${on ? 'is-on' : ''} ${tone}`}>
    <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
    <i aria-hidden="true" />
    <span>{label}</span>
  </label>
);

/* Same props as before: { studentId } */
const SubmitClasses = ({ studentId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);

  const [showing, setShowing] = useState(null); // submission row
  const [editing, setEditing] = useState(null); // submission row
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [deleting, setDeleting] = useState(null); // submission row
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  // ---------- Load (same endpoints) ----------
  // Standard / Mastery: which type each course is (+ the student's balance), from the Payments numbers
  const [typeInfo, setTypeInfo] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/api/admin/studentprofile/sessiontypes/${studentId}`, auth())
      .then((res) => setTypeInfo(res.data))
      .catch(() => setTypeInfo(null));
  }, [studentId, submissions]);

  const fetchData = useCallback(async () => {
    const [submissionsResponse, modulesResponse] = await Promise.all([
      axios.get(`${API}/api/admin/studentprofile/submitclasses/${studentId}`, auth()),
      axios.get(`${API}/api/admin/studentprofile/modules/${studentId}`, auth()),
    ]);
    setSubmissions(Array.isArray(submissionsResponse.data) ? submissionsResponse.data : []);
    setModules(modulesResponse.data?.modules || []);
    setCourses(modulesResponse.data?.courses || []);
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchData()
      .catch((err) => {
        if (!cancelled) setLoadError(errText(err, 'We couldn’t load the submissions.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchData, reload]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Escape closes the top window
  useEffect(() => {
    if (!showing && !editing && !createOpen && !deleting) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (deleting) {
        if (!deleteBusy) setDeleting(null);
        return;
      }
      if (editing && !saving) setEditing(null);
      if (createOpen && !creating) setCreateOpen(false);
      if (showing) setShowing(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showing, editing, createOpen, deleting, saving, creating, deleteBusy]);

  // ---------- Names (compare as text: ids can come as numbers or strings) ----------
  const getModuleName = (moduleId) => {
    const m = modules.find((x) => String(x.Id) === String(moduleId));
    return m ? m.ModuleName : moduleId || '—';
  };
  const getCourseName = (courseId) => {
    const c = courses.find((x) => String(x.Id) === String(courseId));
    return c ? c.CourseName : courseId || '—';
  };
  const coursesOf = (moduleId) => courses.filter((c) => String(c.IdModule) === String(moduleId));

  // ---------- Create skip class (same endpoint + same payload) ----------
  const openCreate = () => {
    setCreateFormData(EMPTY_CREATE);
    setCreateError('');
    setCreateOpen(true);
  };

  // projects already done (in a class or skipped): can't be skipped again
  const doneIds = new Set(submissions.filter((x) => Number(x.IdClassStatus) === 0).map((x) => String(x.IdClass)));
  const toggleSkip = (id) =>
    setCreateFormData((p) => ({ ...p, IdClasses: p.IdClasses.includes(id) ? p.IdClasses.filter((x) => x !== id) : [...p.IdClasses, id] }));

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const chosen = coursesOf(createFormData.IdModule).filter((c) => createFormData.IdClasses.includes(String(c.Id)));
    if (!createFormData.IdModule || !chosen.length) {
      setCreateError('Choose the module and at least one project.');
      return;
    }
    setCreating(true);
    setCreateError('');
    let added = 0;
    try {
      // one "Skipped class" record per project (same endpoint + same payload as before), a second apart so they keep their order
      const base = Date.now();
      for (const [i, course] of chosen.entries()) {
        await axios.post(
          `${API}/api/admin/studentprofile/createsubmission`,
          {
            IdStudent: studentId,
            IdTeacher: SKIP_TEACHER_ID,
            IdModule: createFormData.IdModule,
            IdClass: course.Id,
            IdSchedule: 0,
            ClassDateTime: new Date(base + i * 1000).toISOString(),
            SubmitClass: 0,
            Rating: 5,
            Details: 'Skipped class',
            approved: 0,
            fullpay: 2,
            SessionRate: 0,
            SessionFees: 0,
            IdClassStatus: 0,
            TeacherDuration: 0,
            StudentDuration: 0,
            AdminEvaluation: '',
          },
          auth()
        );
        added += 1;
      }
      await fetchData();
      setCreateOpen(false);
      setToast(added === 1 ? `Skipped: ${chosen[0].CourseName}` : `${added} projects marked as skipped`);
    } catch (err) {
      console.error('Create submission error:', err);
      if (added) await fetchData().catch(() => {});
      setCreateError(`${added ? `${added} added, then: ` : ''}${errText(err, 'Failed to add the skipped projects.')}`);
    } finally {
      setCreating(false);
    }
  };

  // ---------- Edit (same endpoint + same fields) ----------
  const openEdit = (submission) => {
    setShowing(null);
    setEditError('');
    setEditing(submission);
    setFormData({
      Id: submission.Id,
      IdModule: submission.IdModule,
      IdClass: submission.IdClass,
      SubmitClass: submission.SubmitClass,
      Rating: submission.Rating,
      Details: submission.Details,
      approved: submission.approved,
      IdClassStatus: classStatus(submission.IdClassStatus),
      fullpay: submission.fullpay,
      SessionRate: submission.SessionRate,
      SessionFees: submission.SessionFees,
      TeacherDuration: submission.TeacherDuration || 0,
      StudentDuration: submission.StudentDuration || 0,
      AdminEvaluation: submission.AdminEvaluation || '',
    });
  };

  const set = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'TeacherDuration' || name === 'StudentDuration') {
      if (value === '' || DURATION_RE.test(value)) set(name, value);
      return;
    }
    set(name, value);
  };

  const durationValue = (v) => (typeof v === 'number' ? formatDurationDisplay(v) : v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.Details || '').trim()) {
      setEditError('Please write the details.');
      return;
    }
    const teacherDuration =
      typeof formData.TeacherDuration === 'string' ? parseDurationToSeconds(formData.TeacherDuration) : formData.TeacherDuration;
    const studentDuration =
      typeof formData.StudentDuration === 'string' ? parseDurationToSeconds(formData.StudentDuration) : formData.StudentDuration;
    const submitData = { ...formData, TeacherDuration: teacherDuration, StudentDuration: studentDuration };

    setSaving(true);
    setEditError('');
    try {
      await axios.put(`${API}/api/admin/studentprofile/updatesubmission/${editing.Id}`, submitData, auth());
      await fetchData();
      setEditing(null);
      setToast('Submission saved');
    } catch (err) {
      setEditError(errText(err, 'Couldn’t save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoint) ----------
  const askDelete = (submission) => {
    setDeleteError('');
    setDeleting(submission);
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/studentprofile/deletesubmission/${deleting.Id}`, auth());
      await fetchData();
      setDeleting(null);
      setShowing(null);
      setToast('Submission deleted');
    } catch (err) {
      setDeleteError(errText(err, 'Couldn’t delete. Please try again.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- Counts ----------
  const sorted = [...submissions].sort(
    (a, b) => (parse(b.ClassDateTime)?.getTime() || 0) - (parse(a.ClassDateTime)?.getTime() || 0)
  );
  // Same date AND time = one session; the 2nd, 3rd… are extra projects done in that session
  const sessionKey = (s) => {
    const d = parse(s.ClassDateTime);
    return d ? String(d.getTime()) : `x-${s.Id}`;
  };
  const sessions = [];
  const byKey = {};
  sorted.forEach((s) => {
    const k = sessionKey(s);
    if (!byKey[k]) {
      byKey[k] = [];
      sessions.push({ key: k, items: byKey[k] });
    }
    byKey[k].push(s);
  });
  // inside a session, the first one entered is the main project
  sessions.forEach((g) => g.items.sort((a, b) => (Number(a.Id) || 0) - (Number(b.Id) || 0)));
  const extraInfo = (s) => {
    const items = byKey[sessionKey(s)] || [];
    const pos = items.findIndex((x) => x.Id === s.Id);
    return { total: items.length, pos, isExtra: pos > 0 };
  };
  const extraCount = sessions.reduce((n, g) => n + g.items.length - 1, 0);
  const counted = submissions.filter((s) => Number(s.SubmitClass) === 1).length;
  const payCount = (n) => submissions.filter((s) => Number(s.fullpay) === n).length;
  // sessions taken per type: one per class (its main project), skipped projects don't count
  const takenByType = { standard: 0, mastery: 0 };
  if (typeInfo?.courseTypes) {
    sessions.forEach((g) => {
      const main = g.items[0];
      if (!main || String(main.Details || '').trim() === 'Skipped class') return;
      takenByType[typeInfo.courseTypes[String(main.IdModule)] === 'mastery' ? 'mastery' : 'standard'] += 1;
    });
  }

  // ---------- States ----------
  if (loading) {
    return (
      <div className="rmasb" aria-busy="true">
        <div className="rmasb-skel rmasb-skel--bar" />
        <div className="rmasb-skel" />
        <div className="rmasb-skel" />
        <div className="rmasb-skel" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rmasb">
        <div className="rmasb-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{loadError}</p>
          <button type="button" className="rmasb-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const detail = showing;

  return (
    <div className="rmasb">
      {/* ---------- Head ---------- */}
      <div className="rmasb-head">
        <ul className="rmasb-stats">
          <li>
            <b>{sessions.length}</b> sessions
          </li>
          {extraCount > 0 && (
            <li className="is-extra">
              <b>+{extraCount}</b> extra projects
            </li>
          )}
          <li className="is-info">
            <b>{counted}</b> counted
          </li>
          {typeInfo?.courseTypes && (
            <>
              <li className="is-standard" title={`Paid: ${typeInfo.types.standard.bought} · left: ${typeInfo.types.standard.left}`}>
                <b>{takenByType.standard}</b> Standard taken
              </li>
              <li className="is-mastery" title={`Paid: ${typeInfo.types.mastery.bought} · left: ${typeInfo.types.mastery.left}`}>
                <b>{takenByType.mastery}</b> Mastery taken
              </li>
            </>
          )}
          <li className="is-good">
            <b>{payCount(0)}</b> full
          </li>
          <li className="is-warn">
            <b>{payCount(1)}</b> half
          </li>
          <li className="is-bad">
            <b>{payCount(2)}</b> no pay
          </li>
        </ul>
        <div className="rmasb-head-actions">
          <button type="button" className="rmasb-btn" onClick={openCreate}>
            <FontAwesomeIcon icon={faForwardStep} /> Add skipped projects
          </button>
        </div>
      </div>

      {/* ---------- List ---------- */}
      {sorted.length === 0 ? (
        <div className="rmasb-empty">
          <FontAwesomeIcon icon={faClipboardCheck} />
          <p>No submissions yet.</p>
        </div>
      ) : (
        <ul className="rmasb-list">
          {sessions.map((g) => (
            <li key={g.key} className={`rmasb-session ${g.items.length > 1 ? 'has-extra' : ''}`}>
              {g.items.map((s, i) => {
                const d = parse(s.ClassDateTime);
                const isCounted = Number(s.SubmitClass) === 1;
                return (
                  <div key={s.Id ?? `${s.IdClass}-${i}`} className={`rmasb-row ${i > 0 ? 'is-extra' : ''}`}>
                    <button type="button" className="rmasb-main" onClick={() => setShowing(s)}>
                      {i === 0 ? (
                        <span className="rmasb-when">
                          <strong>{d ? format(d, 'MMM dd, yyyy') : '—'}</strong>
                          <span>{d ? format(d, 'hh:mm a') : ''}</span>
                          {g.items.length > 1 && <em className="rmasb-count">{g.items.length} projects</em>}
                        </span>
                      ) : (
                        <span className="rmasb-when rmasb-when--extra">
                          <em className="rmasb-extra">
                            <FontAwesomeIcon icon={faPlus} /> Extra project
                          </em>
                          <span>same session</span>
                        </span>
                      )}
                      <span className="rmasb-what">
                        <strong>{getCourseName(s.IdClass)}</strong>
                        <span>{getModuleName(s.IdModule)}</span>
                        {s.AdminEvaluation && (
                          <em className="rmasb-note">
                            <FontAwesomeIcon icon={faNoteSticky} />
                            <span>{s.AdminEvaluation.split('\n')[0] || s.AdminEvaluation}</span>
                          </em>
                        )}
                      </span>
                    </button>
                    <span className="rmasb-tags">
                      <span className={`rmasb-pill ${isCounted ? 'is-info' : 'is-muted'}`}>{isCounted ? 'Counted' : 'Not counted'}</span>
                      <PayPill value={s.fullpay} />
                      <span className="rmasb-dur" title="Teacher in the Zoom">
                        TJ {formatDurationDisplay(s.TeacherDuration || 0)}
                      </span>
                      <span className="rmasb-dur" title="Student in the Zoom">
                        SJ {formatDurationDisplay(s.StudentDuration || 0)}
                      </span>
                    </span>
                    <span className="rmasb-actions">
                      <button type="button" onClick={() => setShowing(s)} aria-label="Show details" title="Details">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button type="button" onClick={() => openEdit(s)} aria-label="Edit" title="Edit">
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button type="button" className="is-danger" onClick={() => askDelete(s)} aria-label="Delete" title="Delete">
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </li>
          ))}
        </ul>
      )}

      {/* ---------- Details ---------- */}
      {detail && !editing && !deleting && (
        <div className="rmasb-overlay" onMouseDown={(e) => e.target === e.currentTarget && setShowing(null)}>
          <div className="rmasb-modal" role="dialog" aria-modal="true" aria-labelledby="rmasb-show-title">
            <div className="rmasb-modal-head">
              <div>
                <h2 id="rmasb-show-title">{getCourseName(detail.IdClass)}</h2>
                <p>
                  {getModuleName(detail.IdModule)} · {fmtDateTime(detail.ClassDateTime)}
                </p>
                {extraInfo(detail).total > 1 && (
                  <p className="rmasb-session-note">
                    {extraInfo(detail).isExtra
                      ? `Extra project · ${extraInfo(detail).pos + 1} of ${extraInfo(detail).total} in this session`
                      : `Main project · this session has ${extraInfo(detail).total} projects`}
                  </p>
                )}
              </div>
              <button type="button" className="rmasb-x" onClick={() => setShowing(null)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmasb-modal-body">
              <dl className="rmasb-facts">
                <div>
                  <dt>Count</dt>
                  <dd>{Number(detail.SubmitClass) === 1 ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Approved</dt>
                  <dd>{Number(detail.approved) === 0 ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Class status</dt>
                  <dd>{classStatusLabel(detail.IdClassStatus)}</dd>
                </div>
                <div>
                  <dt>Payment</dt>
                  <dd>
                    <PayPill value={detail.fullpay} />
                  </dd>
                </div>
                <div>
                  <dt>Session rate</dt>
                  <dd>${detail.SessionRate ?? 0}</dd>
                </div>
                <div>
                  <dt>Session fees</dt>
                  <dd>${detail.SessionFees ?? 0}</dd>
                </div>
                <div>
                  <dt>Teacher in Zoom (TJ)</dt>
                  <dd className="rmasb-mono">{formatDurationDisplay(detail.TeacherDuration || 0)}</dd>
                </div>
                <div>
                  <dt>Student in Zoom (SJ)</dt>
                  <dd className="rmasb-mono">{formatDurationDisplay(detail.StudentDuration || 0)}</dd>
                </div>
                <div>
                  <dt>Rating</dt>
                  <dd>
                    <Stars value={detail.Rating} />
                  </dd>
                </div>
              </dl>
              <div className="rmasb-text">
                <h3>Details</h3>
                <p>{detail.Details || '—'}</p>
              </div>
              <div className="rmasb-text">
                <h3>Admin evaluation</h3>
                <p className={detail.AdminEvaluation ? '' : 'rmasb-dim'}>{detail.AdminEvaluation || 'No evaluation'}</p>
              </div>
            </div>
            <div className="rmasb-modal-foot">
              <button type="button" className="rmasb-btn rmasb-btn--ghost is-danger" onClick={() => askDelete(detail)}>
                <FontAwesomeIcon icon={faTrashCan} /> Delete
              </button>
              <span className="rmasb-grow" />
              <button type="button" className="rmasb-btn rmasb-btn--ghost" onClick={() => setShowing(null)}>
                Close
              </button>
              <button type="button" className="rmasb-btn" onClick={() => openEdit(detail)}>
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Edit ---------- */}
      {editing && (
        <div className="rmasb-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && setEditing(null)}>
          <form className="rmasb-modal" role="dialog" aria-modal="true" aria-labelledby="rmasb-edit-title" onSubmit={handleSubmit}>
            <div className="rmasb-modal-head">
              <div>
                <h2 id="rmasb-edit-title">Edit submission</h2>
                <p>{fmtDateTime(editing.ClassDateTime)}</p>
              </div>
              <button type="button" className="rmasb-x" onClick={() => setEditing(null)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmasb-modal-body">
              <div className="rmasb-two">
                <label className="rmasb-field">
                  <span>Module</span>
                  <select
                    name="IdModule"
                    value={formData.IdModule ?? ''}
                    onChange={(e) => setFormData((p) => ({ ...p, IdModule: e.target.value, IdClass: '' }))}
                  >
                    <option value="">Select module</option>
                    {modules.map((m) => (
                      <option key={m.Id} value={m.Id}>
                        {m.ModuleName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rmasb-field">
                  <span>Class</span>
                  <select name="IdClass" value={formData.IdClass ?? ''} onChange={handleInputChange} disabled={!formData.IdModule} required>
                    <option value="">Select class</option>
                    {coursesOf(formData.IdModule).map((c) => (
                      <option key={c.Id} value={c.Id}>
                        {c.CourseName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rmasb-switches">
                <Switch
                  on={Number(formData.SubmitClass) === 1}
                  onChange={(on) => set('SubmitClass', on ? 1 : 0)}
                  label="Count this class"
                  tone="is-info"
                />
                {/* same as before: ticked = approved 0 */}
                <Switch
                  on={Number(formData.approved) === 0}
                  onChange={(on) => set('approved', on ? 0 : 1)}
                  label="Approved"
                  tone="is-good"
                />
              </div>

              <div className="rmasb-field">
                <span>Payment</span>
                <div className="rmasb-seg" role="radiogroup" aria-label="Payment">
                  {PAY.map(([n, label, tone]) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={Number(formData.fullpay) === n}
                      className={`${tone} ${Number(formData.fullpay) === n ? 'is-on' : ''}`}
                      onClick={() => set('fullpay', n)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rmasb-two">
                <label className="rmasb-field">
                  <span>Class status</span>
                  <select name="IdClassStatus" value={formData.IdClassStatus} onChange={handleInputChange} required>
                    <option value="0">Completed</option>
                    <option value="1">Not completed</option>
                    {/* an unexpected value from the database stays visible instead of showing as Completed */}
                    {![0, 1].includes(classStatus(formData.IdClassStatus)) && (
                      <option value={formData.IdClassStatus}>{classStatusLabel(formData.IdClassStatus)}</option>
                    )}
                  </select>
                </label>
                <label className="rmasb-field">
                  <span>Rating</span>
                  <select name="Rating" value={formData.Rating ?? ''} onChange={handleInputChange}>
                    <option value="">Select rating</option>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>
                        {'★'.repeat(n)} {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rmasb-two">
                <label className="rmasb-field">
                  <span>Session rate ($)</span>
                  <input type="number" step="0.01" inputMode="decimal" name="SessionRate" value={formData.SessionRate ?? ''} onChange={handleInputChange} required />
                </label>
                <label className="rmasb-field">
                  <span>Session fees ($)</span>
                  <input type="number" step="0.01" inputMode="decimal" name="SessionFees" value={formData.SessionFees ?? ''} onChange={handleInputChange} required />
                </label>
              </div>

              <div className="rmasb-two">
                <label className="rmasb-field">
                  <span>Teacher in Zoom (TJ)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="TeacherDuration"
                    className="rmasb-mono"
                    value={durationValue(formData.TeacherDuration)}
                    onChange={handleInputChange}
                    placeholder="MM:SS"
                  />
                  <small>Minutes:seconds, e.g. 52:18</small>
                </label>
                <label className="rmasb-field">
                  <span>Student in Zoom (SJ)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="StudentDuration"
                    className="rmasb-mono"
                    value={durationValue(formData.StudentDuration)}
                    onChange={handleInputChange}
                    placeholder="MM:SS"
                  />
                  <small>Minutes:seconds, e.g. 44:45</small>
                </label>
              </div>

              <label className="rmasb-field">
                <span>Details *</span>
                <textarea name="Details" rows={3} value={formData.Details ?? ''} onChange={handleInputChange} required />
              </label>

              <label className="rmasb-field">
                <span>Admin evaluation</span>
                <textarea
                  name="AdminEvaluation"
                  rows={3}
                  value={formData.AdminEvaluation}
                  onChange={handleInputChange}
                  placeholder="Payment decision, attendance issues, anything to remember…"
                />
                <small>Only admins see this.</small>
              </label>

              {editError && (
                <p className="rmasb-error" role="alert">
                  {editError}
                </p>
              )}
            </div>
            <div className="rmasb-modal-foot">
              <button type="button" className="rmasb-btn rmasb-btn--ghost" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmasb-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Add skipped class ---------- */}
      {createOpen && (
        <div className="rmasb-overlay" onMouseDown={(e) => e.target === e.currentTarget && !creating && setCreateOpen(false)}>
          <form className="rmasb-modal rmasb-modal--small" role="dialog" aria-modal="true" aria-labelledby="rmasb-create-title" onSubmit={handleCreateSubmit}>
            <div className="rmasb-modal-head">
              <h2 id="rmasb-create-title">Mark projects as skipped</h2>
              <button type="button" className="rmasb-x" onClick={() => setCreateOpen(false)} disabled={creating} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmasb-modal-body">
              <label className="rmasb-field">
                <span>Module *</span>
                <select
                  name="IdModule"
                  value={createFormData.IdModule}
                  onChange={(e) => setCreateFormData({ IdModule: e.target.value, IdClasses: [] })}
                  required
                >
                  <option value="">Select module</option>
                  {modules.map((m) => (
                    <option key={m.Id} value={m.Id}>
                      {m.ModuleName}
                    </option>
                  ))}
                </select>
              </label>
              {createFormData.IdModule && (() => {
                const list = coursesOf(createFormData.IdModule);
                const open = list.filter((c) => !doneIds.has(String(c.Id))).map((c) => String(c.Id));
                return (
                  <div className="rmasb-field">
                    <span>Projects to mark as skipped *</span>
                    <div className="rmasb-skip-tools">
                      <button type="button" onClick={() => setCreateFormData((p) => ({ ...p, IdClasses: open.slice(0, 8) }))}>
                        First 8 not done
                      </button>
                      <button type="button" onClick={() => setCreateFormData((p) => ({ ...p, IdClasses: open }))}>
                        All not done
                      </button>
                      <button type="button" onClick={() => setCreateFormData((p) => ({ ...p, IdClasses: [] }))}>
                        None
                      </button>
                    </div>
                    <ul className="rmasb-skip-list">
                      {list.map((c, i) => {
                        const id = String(c.Id);
                        const done = doneIds.has(id);
                        return (
                          <li key={id} className={done ? 'is-done' : ''}>
                            <label>
                              <input
                                type="checkbox"
                                checked={done || createFormData.IdClasses.includes(id)}
                                disabled={done || creating}
                                onChange={() => toggleSkip(id)}
                              />
                              <span>
                                {i + 1}. {c.CourseName}
                                {done && <small> · already done</small>}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })()}

              <div className="rmasb-info">
                <p>Each project gets a record dated now, so it counts for the course without a lesson (shown in grey as “Skipped”):</p>
                <ul>
                  <li>Count: No</li>
                  <li>Payment: No pay ($0)</li>
                  <li>Details: “Skipped class”</li>
                </ul>
              </div>

              {createError && (
                <p className="rmasb-error" role="alert">
                  {createError}
                </p>
              )}
            </div>
            <div className="rmasb-modal-foot">
              <button type="button" className="rmasb-btn rmasb-btn--ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </button>
              <button type="submit" className="rmasb-btn" disabled={creating}>
                <FontAwesomeIcon icon={faPlus} />{' '}
                {creating
                  ? 'Adding…'
                  : createFormData.IdClasses.length > 1
                  ? `Skip ${createFormData.IdClasses.length} projects`
                  : 'Skip this project'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {deleting && (
        <div className="rmasb-overlay" onMouseDown={(e) => e.target === e.currentTarget && !deleteBusy && setDeleting(null)}>
          <div className="rmasb-dialog" role="alertdialog" aria-modal="true" aria-labelledby="rmasb-del-title">
            <span className="rmasb-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="rmasb-del-title">Delete this submission?</h2>
            <p>
              {getCourseName(deleting.IdClass)} · {fmtDateTime(deleting.ClassDateTime)}. This can’t be undone.
            </p>
            {deleteError && (
              <p className="rmasb-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="rmasb-dialog-actions">
              <button type="button" className="rmasb-btn rmasb-btn--ghost" onClick={() => setDeleting(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type="button" className="rmasb-btn rmasb-btn--danger" onClick={handleDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmasb-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default SubmitClasses;